// Request paths (review of T10). Ids from the URL or from data end up in API paths
// (/tasks/:taskId/actions/:action). A value such as "../admin/users/5" or "%2e%2e" would let the
// browser normalise the request onto another endpoint with the person's own cookie and a valid
// Origin (client-side path traversal), so every path is checked at the one place that sends it.

// Path part only: starts with one "/", no backslash, whitespace, query or fragment marker.
// Matches "/me", "/tasks/T-OPS-026", "/search?q=a b"; rejects "//evil.example", "me", "/a\b",
// "/a b", "/a#b".
// https://regex101.com/?regex=%5E%5C%2F%28%3F%21%5C%2F%29%5B%5E%5C%5C%5Cs%23%3F%5D%2A%24&testString=%2Fme%0A%2F%2Fevil.example%0Ame%0A%2Fa%5Cb&flavor=javascript
const SAFE_PATH = /^\/(?!\/)[^\\\s#?]*$/;
// A dot segment as the URL parser sees it: ".", "..", and their %2e spellings.
// Matches ".", "..", "%2e", ".%2E", "%2e%2e"; rejects "...", "a.", "%2ea".
// https://regex101.com/?regex=%5E(%3F%3A%5C.%7C%252e)%7B1%2C2%7D%24&flags=i&testString=.%0A..%0A%252e%0A...%0Aa.&flavor=javascript
const DOT_SEGMENT = /^(?:\.|%2e){1,2}$/i;
const FIRST_PRINTABLE = 0x20;
const DELETE_CHAR = 0x7f;

function hasControlChar(text: string): boolean {
  return [...text].some((ch) => {
    const code = ch.charCodeAt(0);
    return code < FIRST_PRINTABLE || code === DELETE_CHAR;
  });
}

/** True when `path` (with an optional query string) is a plain API path with no dot segments. */
export function isSafePath(path: string): boolean {
  const queryAt = path.indexOf('?');
  const pathPart = queryAt === -1 ? path : path.slice(0, queryAt);
  if (!SAFE_PATH.test(pathPart) || hasControlChar(path) || path.includes('#')) return false;
  return !pathPart.split('/').some((segment) => DOT_SEGMENT.test(segment));
}

/** Throws before the network when a caller built a path that could leave its endpoint. */
export function assertSafePath(path: string): string {
  if (!isSafePath(path)) throw new RangeError('Refused an API path with an unsafe segment');
  return path;
}

/** Encodes one value (an id, a code) for use as a single path segment. */
export function pathSegment(value: string | number): string {
  const text = String(value);
  if (text === '' || DOT_SEGMENT.test(text)) {
    throw new RangeError('A path segment cannot be empty, "." or ".."');
  }
  return encodeURIComponent(text);
}
